/**
 * Routes.
 *
 * Code-based rather than file-based, because there are six of them and a
 * generated route tree would be more machinery than the app has routes.
 *
 * Gallery filters live in the URL as typed search params, so a filtered view is
 * a link a friend can send: `/g/grp_…?folder=fld_…&year=2026&kind=photo`.
 */

import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import type { GallerySort } from "@fc/core";
import { AppShell } from "./AppShell.js";
import { RootLayout } from "./RootLayout.js";
import { ResetPasswordPage } from "../features/auth/ResetPasswordPage.js";
import { RecoverPage } from "../features/auth/RecoverPage.js";
import { LoginPage } from "../features/auth/LoginPage.js";
import { RegisterPage } from "../features/auth/RegisterPage.js";
import { VerifyEmailPage } from "../features/auth/VerifyEmailPage.js";
import { JoinPage } from "../features/auth/JoinPage.js";
import { SharePage } from "../features/share/SharePage.js";
import { GalleryPage } from "../features/gallery/GalleryPage.js";
import { GroupSettingsPage } from "../features/group/GroupSettingsPage.js";
import { AccountPage } from "../features/account/AccountPage.js";
import { WallPage } from "../features/wall/WallPage.js";
import { AdminPage } from "../features/admin/AdminPage.js";
import { HomePage } from "../features/home/HomePage.js";
import { LandingPage } from "../features/landing/LandingPage.js";
import { PrivacyPage, TermsPage } from "../features/legal/LegalPage.js";
import { useAuth } from "../lib/auth.js";

export interface RouterContext {
  /** Resolved before any route renders, so guards do not flash. */
  isAuthenticated: () => boolean;
  isPlatformAdmin: () => boolean;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

/**
 * The root address, which is two different pages.
 *
 * It used to be a child of the authenticated route, so a stranger who typed
 * the address was redirected to a password field and learned nothing about
 * what they had been invited into. Now it is public and branches: the landing
 * page for somebody signed out, the application for somebody signed in.
 *
 * Branching in the component rather than redirecting keeps one URL for both.
 * A marketing page that lives at /welcome is a marketing page nobody links to.
 */
function IndexRoute() {
  const auth = useAuth();

  /* Still asking the server who this is. Rendering the landing page here
     would flash it at somebody who is signed in. */
  if (auth.status === "loading") return null;

  if (!auth.user) return <LandingPage />;

  return (
    <AppShell>
      <HomePage />
    </AppShell>
  );
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: IndexRoute,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: PrivacyPage,
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: TermsPage,
});
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
});

/**
 * The invite entry point. Accepts the code in the path so an invite link can be
 * pasted anywhere, and works for both signed-in users (joins the group) and new
 * ones (creates the account first).
 */
/**
 * Where someone with a code but no link starts.
 *
 * Codes are read off a screen or dictated, so they arrive detached from the
 * invite URL. Without somewhere to type one, a perfectly valid code was
 * unusable. This only validates and forwards to `/join/:code` — the sign-up
 * form itself stays in one place.
 */
const recoverRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/recover",
  component: RecoverPage,
});

const resetPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: "/reset-password", component: ResetPasswordPage });

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  component: RegisterPage,
  // A mailed invitation carries the code in the query, so nobody has to
  // transcribe it. Typing one by hand still works.
  validateSearch: (search: Record<string, unknown>) => ({
    code: typeof search.code === "string" ? search.code : undefined,
  }),
});

/**
 * Confirming an address.
 *
 * The token arrives in the fragment rather than the query, so it is never sent
 * to a server in a request line and never lands in an access log.
 */
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  component: VerifyEmailPage,
});

const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/join/$code",
  component: JoinPage,
});

/**
 * The public share viewer. Deliberately a sibling of `/login`, not a child of
 * the authenticated tree: a stranger following a link must never be bounced to
 * a sign-in screen for photos they were explicitly given.
 */
const shareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/s/$token",
  component: SharePage,
});

/** Everything below requires a session. */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authed",
  component: AppShell,
  beforeLoad: ({ context, location }) => {
    if (!context.isAuthenticated()) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
  },
});

export interface GallerySearch {
  folder?: string;
  kind?: "photo" | "video";
  year?: number;
  favorites?: boolean;
  sort?: GallerySort;
  /** Whose uploads to show. A member id, matching `filter.uploaderId`. */
  by?: string;
  /** Opens the lightbox directly on a photo, so a link can point at one. */
  photo?: string;
  /** Swaps the photo grid for something else that belongs to this group. */
  view?: "albums" | "trash";
  /** How the photo grid is sectioned. Months is the default. */
  groupBy?: "month" | "album";
  /** Tile size. Lives in the URL so a chosen density survives a reload. */
  density?: "comfortable" | "compact" | "large";
}

const galleryRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/g/$groupId",
  component: GalleryPage,
  validateSearch: (search: Record<string, unknown>): GallerySearch => ({
    folder: typeof search.folder === "string" ? search.folder : undefined,
    kind:
      search.kind === "photo" || search.kind === "video"
        ? search.kind
        : undefined,
    year:
      Number.isFinite(Number(search.year)) && search.year
        ? Number(search.year)
        : undefined,
    favorites:
      search.favorites === true || search.favorites === "true"
        ? true
        : undefined,
    sort:
      typeof search.sort === "string"
        ? (search.sort as GallerySort)
        : undefined,
    by: typeof search.by === "string" ? search.by : undefined,
    photo: typeof search.photo === "string" ? search.photo : undefined,
    view:
      search.view === "albums" || search.view === "trash"
        ? search.view
        : undefined,
    groupBy:
      search.groupBy === "album"
        ? "album"
        : search.groupBy === "month"
          ? "month"
          : undefined,
    density:
      search.density === "compact" ||
      search.density === "large" ||
      search.density === "comfortable"
        ? search.density
        : undefined,
  }),
});

const wallRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/g/$groupId/wall",
  component: WallPage,
});

const groupSettingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/g/$groupId/settings",
  component: GroupSettingsPage,
});

/** Your own account, as distinct from any group's settings. */
const accountRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/account",
  component: AccountPage,
});

const adminRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/admin",
  component: AdminPage,
  beforeLoad: ({ context }) => {
    // Cosmetic only — every admin procedure re-checks server-side, and returns
    // NOT_FOUND rather than FORBIDDEN so the panel's existence is not confirmed.
    if (!context.isPlatformAdmin()) throw redirect({ to: "/" });
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  privacyRoute,
  termsRoute,
  registerRoute,
  verifyEmailRoute,
  loginRoute,
  recoverRoute,
  resetPasswordRoute,
  joinRoute,
  shareRoute,
  authedRoute.addChildren([
    galleryRoute,
    wallRoute,
    groupSettingsRoute,
    accountRoute,
    adminRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { isAuthenticated: () => false, isPlatformAdmin: () => false },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  // Scroll position is managed per-gallery so returning from the lightbox lands
  // on the same tile.
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export { Outlet };
